import type {
  AdminPermission as PrismaAdminPermission,
  AgentPermission as PrismaAgentPermission,
} from '@/backend/src/services/other/prisma.js'
import type { CheckIfArraySatisfies } from 'svag-types'
import { z } from 'zod'

export const adminPermissions = [
  'manageAdmins',
  'viewAgents',
  'manageAgents',
  'manageUpgradesPosts',
  'viewUpgradesPosts',
] as const
export const zAdminPermission = z.enum(adminPermissions)
export type AdminPermission = z.output<typeof zAdminPermission>
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const check1: CheckIfArraySatisfies<AdminPermission, PrismaAdminPermission> = true
export const toHumanAdminPermission = (permission: AdminPermission) => {
  return (
    {
      manageAdmins: 'Управление админами',
      viewAgents: 'Просмотр агентов',
      manageAgents: 'Управление агентами',
      manageUpgradesPosts: 'Управление обновлениями',
      viewUpgradesPosts: 'Просмотр обновлений',
    }[permission] || 'Unknown permission'
  )
}
export const adminPermissionsOptions = adminPermissions.map((permission) => ({
  value: permission,
  label: toHumanAdminPermission(permission),
}))

export const agentPermissions = ['useBetaFeatures'] as const
export const zAgentPermission = z.enum(agentPermissions)
export type AgentPermission = z.output<typeof zAgentPermission>
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const check2: CheckIfArraySatisfies<AgentPermission, PrismaAgentPermission> = true
export const toHumanAgentPermission = (permission: AgentPermission) => {
  return (
    {
      useBetaFeatures: 'Использование бета-функционала',
    }[permission] || 'Unknown permission'
  )
}
export const agentPermissionsOptions = agentPermissions.map((permission) => ({
  value: permission,
  label: toHumanAgentPermission(permission),
}))

type MaybeAdmin = {
  permissions: AdminPermission[]
} | null
type MaybeAgent = {
  permissions: AgentPermission[]
} | null

export type SimpleCanAsAdminFn = (admin: MaybeAdmin) => boolean
export type SimpleCanAsAgent = (agent: MaybeAgent) => boolean

export const hasPermissionAsAdmin = (admin: MaybeAdmin, permission: AdminPermission) => {
  if (!admin) {
    return false
  }
  return admin.permissions.includes(permission)
}
export const hasPermissionAsAgent = (agent: MaybeAgent, permission: AgentPermission) => {
  if (!agent) {
    return false
  }
  return agent.permissions.includes(permission)
}

const createSimpleCanAsAdminFn = (permission: AdminPermission): SimpleCanAsAdminFn => {
  return (admin) => {
    return hasPermissionAsAdmin(admin, permission)
  }
}
const createSimpleCanAsAgentFn = (permission: AgentPermission): SimpleCanAsAgent => {
  return (agent) => {
    return hasPermissionAsAgent(agent, permission)
  }
}

// admin

export const canManageAdmins = createSimpleCanAsAdminFn('manageAdmins')
export const canViewAgents = createSimpleCanAsAdminFn('viewAgents')
export const canManageAgents = createSimpleCanAsAdminFn('manageAgents')
export const canManageUpgradesPosts = createSimpleCanAsAdminFn('manageUpgradesPosts')
export const canViewUpgradesPosts = createSimpleCanAsAdminFn('viewUpgradesPosts')

// agent

export const canUseBetaFeatures = createSimpleCanAsAgentFn('useBetaFeatures')
